import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, IconButton, TextField } from "@mui/material";
import apiClient from "../services/apiClient";
import { useDispatch, useSelector } from "react-redux";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MdDelete, MdFilterList, MdFilterListOff } from "react-icons/md";
import { RiEdit2Fill } from "react-icons/ri";
import PlaceholderPhone from "../assets/placeholder-phone.png";

import SearchIcon from "@mui/icons-material/Search";
import PageLoader from "../components/Loading/PageLoader";
import CustomButton from "../components/Button/CustomButton";
import CustomInputBox from "../components/Form/CustomInputBox";
import { setModal } from "../features/modal/ModalSlice";

const ExtensionAllocation = () => {
  const [numberDetails, setNumberDetails] = useState([]);
  const [filter, setFilter] = useState({
    search: "",
    assigned_phone_status: "",
    page: 1,
    pageSize: 50,
  });

  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(true);

  const userInfo = useSelector((state) => state?.userInfo?.user);
  const modal = useSelector((state) => state?.modal?.modal);
  const dispatch = useDispatch();

  useEffect(() => {
    fetchAllocatedExtension();
  }, [filter]);

  useEffect(() => {
    if (modal?.isUpdated?.isUpdatedIn === "") handleUpdate(modal?.isUpdated?.data);
  }, [modal?.isUpdated]);

  const fetchAllocatedExtension = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(
        `/assignedPhoneDetails?page=${filter?.page}&page_size=${filter?.pageSize}&company_id=${userInfo?.company_id}&assigned_phone_status=${filter?.assigned_phone_status}`
      );

      const transformedData = response?.data?.map((item) => ({
        username: item?.data.username,
        assigned_phone_status: item?.assigned_phone_status,
        user_name: item?.user_details?.name,
        effective_caller_id_number: item?.data.effective_caller_id_number,
        password: item?.data.password,
        _id: item?._id,
        user_details: item?.user_details,
        data: item?.data,
      }));
      setNumberDetails(transformedData);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "effective_caller_id_number",
        header: "Number",
      },
      {
        accessorKey: "username",
        header: "Extension",
      },
      {
        accessorKey: "user_name",
        header: "Member",
      },
      {
        accessorKey: "assigned_phone_status",
        header: "Assigned",
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: numberDetails,
    enableColumnOrdering: true,
    enableRowSelection: true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableRowActions: true,
    enablePagination: false,
    positionActionsColumn: "last",
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", flexWrap: "nowrap", gap: "8px" }}>
        <IconButton
          title="Edit Assignment"
          onClick={() => handleEditConfigureNumberPopUp(row?.original)}
        >
          <RiEdit2Fill />
        </IconButton>
        <IconButton disabled>
          <MdDelete />
        </IconButton>
      </Box>
    ),
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#F5F6F8",
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: "12px",
        border: "1px solid #e0e0e0",
      },
    },
  });

  const handleShowFilter = () => {
    setShowFilter(!showFilter);
  };

  const handleRequestPopUp = () => {
    dispatch(setModal({ type: "ADD_REQUESTS" }));
  };

  const handleConfigureNumberPopUp = (data = null) => {
    dispatch(setModal({ type: "CONFIGURE_NUMBER", data }));
  };

  const handleEditConfigureNumberPopUp = (data = null) => {
    dispatch(setModal({ type: "EDIT_CONFIGURE_NUMBER", data }));
  };

  const handleUpdate = () => {
    fetchAllocatedExtension();
  };

  return (
    <>
      {loading && <PageLoader text="Loading Numbers" />}

      {numberDetails.length ? (
        <>
          <div className="pageTitle1 p-5">
            <h3>Allocated Number and Extensions</h3>
            <p>{numberDetails?.length} Available</p>
          </div>

          <Box sx={{ padding: 2 }}>
            <div className="d-flex justify-content-between">
              <div className="w-50">
                <CustomInputBox
                  icon={SearchIcon}
                  placeholder="Search Number or Extension"
                  type="text"
                  backgroundColor={"#FFFFFF"}
                  borderRadius="8px"
                />
              </div>

              <div className="d-flex justify-content-end gap-3 mb-4">
                <CustomButton
                  text={(showFilter ? "Hide" : "Show") + " Filter"}
                  color="secondary"
                  iconPosition="left"
                  icon={showFilter ? <MdFilterListOff /> : <MdFilterList />}
                  onClick={handleShowFilter}
                />

                <CustomButton text={"Configure"} onClick={() => handleConfigureNumberPopUp()} />
              </div>
            </div>

            {showFilter && (
              <div className="d-flex justify-content-start gap-3 mb-4">
                <Autocomplete
                  disablePortal
                  options={[]}
                  sx={{ width: 200, backgroundColor: "white", borderRadius: "8px" }}
                  size="small"
                  renderInput={(params) => <TextField {...params} label="Extension" />}
                />

                <Autocomplete
                  disablePortal
                  options={["assigned", "not-assigned"]}
                  value={filter?.assigned_phone_status}
                  onChange={(event, newValue) =>
                    setFilter((prev) => ({ ...prev, assigned_phone_status: newValue || "" }))
                  }
                  name="assigned_phone_status"
                  sx={{ width: 200, backgroundColor: "white", borderRadius: "8px" }}
                  size="small"
                  renderInput={(params) => <TextField {...params} label="Status" />}
                />

                <CustomButton text={"Reset"} variant="text" color="inherit" />
              </div>
            )}

            <MaterialReactTable table={table} />
          </Box>
        </>
      ) : (
        <div className="p-4">
          <div className="bg-white min-vh-100 border px-4 py-3 d-flex align-items-center justify-content-center">
            <div className="reqConnection text-center mb-5">
              <img src={PlaceholderPhone} alt="phone" />
              <h3 className="mt-3">Get a new Greymatterz number</h3>
              <p className="p">
                Click request connection button to
                <br /> request a new number.
              </p>
              <button className="reqBtn mb-4" onClick={handleRequestPopUp}>
                Request Connection
              </button>
              <p>
                A Greymatterz number allows you to make and receive calls in Greymatterz with
                automatic logging.
              </p>
              <p>You can get Greymatterz numbers based in US, Uk and Canada.</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExtensionAllocation;
